import { parseJsonWithNativeSafeNumbers, stringifyLosslessJson } from '@pillar-portal/api-types';
import { useState, useEffect } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { z } from 'zod';

export const JsonEditorValueSchema: z.ZodType<Record<string, unknown>> = z.record(
    z.string(),
    z.unknown()
);

interface JsonEditorProps {
    value: Record<string, unknown>;
    onChange: (value: Record<string, unknown>) => void;
    disabled?: boolean;
    placeholder?: string;
}

export function JsonEditor({
    value,
    onChange,
    disabled = false,
    placeholder = '{}',
}: JsonEditorProps) {
    const [textValue, setTextValue] = useState('');
    const [error, setError] = useState<string | null>(null);

    // Initialize text value from prop
    useEffect(() => {
        setTextValue(Object.keys(value).length === 0 ? '' : stringifyLosslessJson(value, null, 2));
    }, [value]);

    const handleChange = (newText: string) => {
        setTextValue(newText);

        // Clear if empty - use empty object
        if (newText.trim() === '') {
            setError(null);
            onChange({});
            return;
        }

        // Try to parse JSON
        try {
            const parsed: Record<string, unknown> = JsonEditorValueSchema.parse(
                parseJsonWithNativeSafeNumbers(newText)
            );
            setError(null);
            onChange(parsed);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Invalid JSON');
            // Don't call onChange if invalid
        }
    };

    return (
        <div className="space-y-2">
            <textarea
                value={textValue}
                onChange={(e) => handleChange(e.target.value)}
                disabled={disabled}
                placeholder={placeholder}
                className="w-full min-h-[200px] font-mono text-sm p-3 border rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                spellCheck={false}
            />
            {error && (
                <Alert variant="destructive">
                    <AlertDescription>
                        <span className="font-semibold">JSON Error:</span> {error}
                    </AlertDescription>
                </Alert>
            )}
            {!error && textValue && (
                <p className="text-xs text-muted-foreground">
                    Valid JSON ({stringifyLosslessJson(value).length} characters)
                </p>
            )}
        </div>
    );
}
